import React from 'react';
import { Box, Flex, Icon, Text } from '@chakra-ui/react';
import { CurrencyDollar, Receipt, UsersThree, TrendUp, Buildings, IconProps } from 'phosphor-react';
import { UI } from '../../ui';

/** Painel glass tier-1 (mais vivo que UI.glass) — base das seções ricas. */
export function GlassPanel({
  title,
  sub,
  action,
  children,
  p = 5,
}: {
  title?: string;
  sub?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  p?: number;
}) {
  return (
    <Box
      bg="rgba(255,255,255,0.7)"
      backdropFilter="blur(14px) saturate(160%)"
      border="1px solid rgba(255,255,255,0.85)"
      boxShadow="0 12px 34px -18px rgba(15,23,42,0.35)"
      borderRadius="18px"
      p={p}
      position="relative"
      overflow="hidden"
    >
      {(title || action) && (
        <Flex justify="space-between" align="center" mb={4} gap={2}>
          <Box>
            {title && (
              <Text fontSize="13px" fontWeight={800} color={UI.ink}>
                {title}
              </Text>
            )}
            {sub && (
              <Text fontSize="11px" color={UI.faint} fontWeight={600}>
                {sub}
              </Text>
            )}
          </Box>
          {action}
        </Flex>
      )}
      {children}
    </Box>
  );
}

/** KPI HUD com glow + ícone em gradiente (estilo SquadHeader/Pessoas). */
export function KpiTile({
  label,
  value,
  sub,
  icon,
  color,
  info,
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ComponentType<IconProps>;
  color: string;
  info?: React.ReactNode;
}) {
  return (
    <GlassPanel p={0}>
      <Box position="relative" p="16px 18px">
        <Box
          position="absolute"
          top="-50px"
          insetEnd="-30px"
          w="120px"
          h="120px"
          borderRadius="full"
          style={{ background: `radial-gradient(circle, ${color}26, transparent 66%)` }}
          pointerEvents="none"
        />
        <Flex align="flex-start" justify="space-between" mb={3} position="relative">
          <Flex align="center" gap={1}>
            <Text
              fontSize="10.5px"
              fontWeight={700}
              letterSpacing=".9px"
              color={UI.muted}
              textTransform="uppercase"
            >
              {label}
            </Text>
            {info}
          </Flex>
          <Flex
            align="center"
            justify="center"
            w="32px"
            h="32px"
            borderRadius="10px"
            flexShrink={0}
            color="white"
            style={{ background: `linear-gradient(135deg, ${color}, ${color}B0)` }}
          >
            <Icon as={icon} boxSize="17px" weight="bold" />
          </Flex>
        </Flex>
        <Text
          fontSize="26px"
          fontWeight={800}
          letterSpacing="-0.8px"
          color={UI.ink}
          lineHeight="1"
          position="relative"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {value}
        </Text>
        {sub && (
          <Text fontSize="12px" fontWeight={600} mt={2} color={UI.muted} position="relative">
            {sub}
          </Text>
        )}
      </Box>
    </GlassPanel>
  );
}

/** Paleta de séries (mesma do Dashboard). */
export const PALETA = [
  UI.brand,
  UI.pos,
  UI.warn,
  '#8B5CF6',
  '#1AA6B7',
  UI.neg,
  '#EC4899',
  '#0EA5E9',
];

/** Ícone por heurística da chave (reuso do DossieLaudo). */
export const iconeDe = (k: string): React.ComponentType<IconProps> => {
  const s = k.toLowerCase();
  if (s.includes('fatur') || s.includes('receita') || s.includes('venda')) return CurrencyDollar;
  if (s.includes('imposto') || s.includes('carga') || s.includes('tribut')) return Receipt;
  if (s.includes('folha') || s.includes('empreg') || s.includes('colab') || s.includes('pessoal'))
    return UsersThree;
  if (s.includes('margem') || s.includes('result') || s.includes('lucro')) return TrendUp;
  return Buildings;
};
